import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import DataTable from 'react-data-table-component'
import { ArrowLeftIcon } from '@heroicons/react/20/solid'
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/16/solid'
import { ClockIcon } from '@heroicons/react/24/outline'
import Flatpickr from 'react-flatpickr'
import { useLazyQuery } from '@apollo/client'
import { GET_HOURLY_BOOKING_STATISTICS } from 'graphql/reports/queries'
import moment from 'moment'
import { ErrorDialog } from 'components/custom/ResponseDialog'
import loadingImage from '../../assets/images/boat-loading.gif'
import { Divider } from 'components'
import i18n from 'i18next'

const INITIAL_FILTER_STATE = {
    travel_date: {
        start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
        end_date: moment().format('YYYY-MM-DD'),
    },
}

export default function HourlyBookingReport() {
    const navigate = useNavigate()
    const { t } = useTranslation()

    const [filterObj, setFilterObj] = useState(INITIAL_FILTER_STATE)
    const [data, setData] = useState([])
    const [exportLoading, setExportLoading] = useState(false)
    const [showErrDialog, setShowErrDialog] = useState(false)
    const [dialogMessage, setDialogMessage] = useState('')
    const [filterApplied, setFilterApplied] = useState(false)

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            setData([])
            setFilterApplied(false)
        }
    }, [])

    const [getHourlyStats, { loading: reportLoading }] = useLazyQuery(GET_HOURLY_BOOKING_STATISTICS, {
        fetchPolicy: 'network-only',
        onCompleted: data => {
            if (data.hourlyBookingStatistics.success && data.hourlyBookingStatistics.code === '200') {
                setData(data.hourlyBookingStatistics.booking_statistics)
            } else {
                handleError(data.hourlyBookingStatistics.message)
            }
        },
        onError: error => handleError(error.message),
    })

    const handleError = message => {
        setDialogMessage(message || t('Failed to fetch hourly booking statistics'))
        setShowErrDialog(true)
    }

    const fetchHourlyStats = (filterData = filterObj) => {
        getHourlyStats({
            variables: {
                startDate: filterData.travel_date.start_date,
                endDate: filterData.travel_date.end_date,
            },
        })
    }

    const handleFilter = () => {
        setFilterApplied(true)
        fetchHourlyStats(filterObj)
    }

    const handleClear = () => {
        const resetFilter = {
            travel_date: {
                start_date: moment().subtract(1, 'months').format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
            },
        }
        setFilterObj(resetFilter)
        setFilterApplied(false)
        setData([]) // Clear existing data
    }

    const handleExportCSV = () => {
        try {
            setExportLoading(true)
            const headers = ['Booking Hour', 'Total Bookings', 'Total Passengers', 'Total Trips']
            const csvContent = [
                headers.join(','),
                ...data.map(row => {
                    // Sanitize booking hour label to replace problematic characters
                    const sanitizedHourLabel = row.booking_hour_label.replace(/â€"|–|—/g, '-')
                    return [sanitizedHourLabel, row.total_bookings, row.total_passengers, row.total_trips].join(',')
                }),
            ].join('\n')

            const blob = new Blob([csvContent], { type: 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `hourly-booking-statistics-${moment().format('YYYY-MM-DD')}.csv`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)
        } catch (error) {
            handleError(error.message)
        } finally {
            setExportLoading(false)
        }
    }

    const columns = useMemo(
        () => [
            {
                name: t('Booking Hour'),
                selector: row => row.booking_hour_label,
                sortable: true,
            },
            {
                name: t('Total Bookings'),
                selector: row => row.total_bookings,
                sortable: true,
            },
            {
                name: t('Total Passengers'),
                selector: row => row.total_passengers,
                sortable: true,
            },
            {
                name: t('Total Trips'),
                selector: row => row.total_trips,
                sortable: true,
            },
        ],
        [t]
    )

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: '#f9fafb',
                color: '#111827',
                fontWeight: 600,
                fontSize: '0.875rem',
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
        cells: {
            style: {
                paddingLeft: '1rem',
                paddingRight: '1rem',
                minWidth: 'auto',
                textAlign: i18n.language === 'ar' ? 'right' : 'left',
            },
        },
    }

    return (
        <>
            <div className="mb-6 flex justify-between items-center">
                <button
                    onClick={() => navigate('/')}
                    type="button"
                    className="inline-flex items-center gap-x-1.5 rounded-md text-sm"
                >
                    <ArrowLeftIcon className="-ml-0.5 h-5 w-5 rtl:rotate-180" />
                    {t('Home')}
                </button>
            </div>

            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <h1 className="text-base font-semibold leading-7 text-gray-900 flex items-center">
                        <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 bg-[#e9eff1]">
                            <ClockIcon className="w-[20px] h-[20px] stroke-[#285f74]" />
                        </div>
                        <div>
                            <span className="ms-3">{t('Hourly Boooking Performance')}</span>
                            <p className="ms-3 text-sm font-normal leading-6 text-gray-500">
                                {t('Track hourly booking patterns')}
                            </p>
                        </div>
                    </h1>

                    <div className="mt-6 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Date Range')}</label>
                            <Flatpickr
                                options={{
                                    mode: 'range',
                                    dateFormat: 'Y-m-d',
                                    defaultDate: [
                                        moment().subtract(1, 'months').format('YYYY-MM-DD'),
                                        moment().format('YYYY-MM-DD'),
                                    ],
                                }}
                                value={[filterObj.travel_date.start_date, filterObj.travel_date.end_date]}
                                onChange={dates => {
                                    setFilterObj({
                                        travel_date: {
                                            start_date: dates[0] ? moment(dates[0]).format('YYYY-MM-DD') : null,
                                            end_date: dates[1] ? moment(dates[1]).format('YYYY-MM-DD') : null,
                                        },
                                    })
                                }}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                            />
                        </div>

                        <div className="sm:col-span-2 flex items-end gap-2">
                            <button
                                onClick={handleFilter}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9]"
                            >
                                {t('Filter')}
                            </button>
                            <button
                                onClick={handleClear}
                                className="inline-flex items-center justify-center rounded-md bg-white px-6 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                                {t('Clear')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <Divider className="my-4 mb-6" />

            {reportLoading ? (
                <div className="flex flex-col justify-center items-center bg-white h-96">
                    <img alt="loading" src={loadingImage} className="w-[250px]" />
                    <p className="pt-3 text-primary font-semibold">{t('Loading, please wait...')}</p>
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center mb-4 -mt-3">
                        <p className="font-semibold">{t('Hourly Statistics')}</p>
                        {data.length > 0 && (
                            <button
                                onClick={handleExportCSV}
                                disabled={exportLoading}
                                className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/[0.9] disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {exportLoading ? (
                                    <>
                                        <ArrowPathIcon className="-ml-0.5 mr-1.5 h-5 w-5 animate-spin" />
                                        {t('Exporting...')}
                                    </>
                                ) : (
                                    <>
                                        <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" />
                                        {t('Export to CSV')}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                    {!filterApplied || data.length === 0 ? (
                        <div className="text-center py-24 bg-gray-50 mt-0 rounded-lg w-full">
                            <svg
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true"
                                className="mx-auto h-12 w-12 text-gray-400"
                            >
                                <path
                                    d="M16.6725 16.6412L21 21M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                                    strokeWidth={2}
                                    vectorEffect="non-scaling-stroke"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('No records found!')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('Please add or modify your search criteria and try again.')}
                            </p>
                            <div className="mt-6"></div>
                        </div>
                    ) : (
                        <DataTable columns={columns} data={data} customStyles={customStyles} />
                    )}
                </>
            )}

            {showErrDialog && (
                <ErrorDialog
                    status={true}
                    title={t('Error')}
                    message={dialogMessage}
                    onClose={() => setShowErrDialog(false)}
                />
            )}
        </>
    )
}
